import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { tokens } from '@/theme'
import type { Domain } from '@/domain/types'
import { useDomains } from '@/api/hooks/useDomains'
import { useAuthContext } from '@/auth/AuthProvider'
import { PageHeader } from '@/components/layout/PageHeader'
import { SearchBox } from '@/components/layout/SearchBox'
import { Pagination } from '@/components/layout/Pagination'
import { SkeletonList, ErrorPanel } from '@/components/feedback'

interface Props {
  onSelectDomain: (domainId: number) => void
}

/**
 * Domains tab landing — paginated list with text search and (admin only)
 * an `includeInactive` toggle. Clicking a row drills into the domain
 * detail; the parent App owns the navigation state.
 */
export function DomainsView({ onSelectDomain }: Props) {
  const { authState } = useAuthContext()
  const isAdmin = authState.status === 'authenticated' && authState.roles.isAdministrator

  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)

  const query = useDomains({ page, pageSize: 25, q, includeInactive })

  const onSearchChange = (next: string): void => {
    setQ(next)
    setPage(1) // resetting the page so a new search starts at page 1
  }

  return (
    <div
      className="orion-pad"
      style={{
        maxWidth: tokens.containerMax,
        margin: '0 auto',
        padding: `48px ${tokens.containerPadX}px`,
      }}
    >
      <PageHeader
        eyebrow="Business Architecture"
        title="Domains"
        lede="Top-level groupings of business capabilities. Open one to see its capability tree and the initiatives touching it."
        actions={
          <>
            {isAdmin && (
              <ToggleChip
                active={includeInactive}
                onClick={() => setIncludeInactive(v => !v)}
                label="Include inactive"
              />
            )}
            <SearchBox value={q} onChange={onSearchChange} placeholder="Search domains…" />
          </>
        }
      />

      {query.status === 'loading' && <SkeletonList rows={6} rowHeight={68} />}
      {query.status === 'error' && <ErrorPanel error={query.error} />}
      {query.status === 'success' && (
        <>
          {query.data.data.length === 0 ? (
            <EmptyState search={q} />
          ) : (
            <ul style={{ listStyle: 'none', display: 'grid', gap: 8, padding: 0, margin: 0 }}>
              {query.data.data.map(d => (
                <DomainRow key={d.domainId} domain={d} onClick={() => onSelectDomain(d.domainId)} />
              ))}
            </ul>
          )}
          <Pagination
            page={query.data.page}
            pageSize={query.data.pageSize}
            total={query.data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Row + small helpers
// ---------------------------------------------------------------------------

function DomainRow({ domain, onClick }: { domain: Domain; onClick: () => void }) {
  const [hover, setHover] = useState(false)

  return (
    <li>
      <button
        className="orion-sm-row"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto',
          alignItems: 'center',
          gap: 24,
          width: '100%',
          textAlign: 'left',
          padding: '18px 20px',
          background: hover ? tokens.paper2 : tokens.paper,
          border: `1px solid ${hover ? tokens.line2 : tokens.line}`,
          borderRadius: tokens.radiusMd,
          cursor: 'pointer',
          fontFamily: tokens.fontDisplay,
          transition: `background ${tokens.durFast} ${tokens.easeStandard}, border-color ${tokens.durFast} ${tokens.easeStandard}`,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 16,
              fontWeight: tokens.weightDemi,
              color: tokens.ink,
              marginBottom: 4,
            }}
          >
            {domain.domainName}
            {!domain.isActive && <InactiveBadge />}
          </div>
          {domain.domainDescription && (
            <div
              style={{
                fontSize: 13,
                color: tokens.ink3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {domain.domainDescription}
            </div>
          )}
        </div>
        {/* Two counts and a chevron are 270px of a 360px phone. The one that
            survives is the one the row is a route to more of. */}
        <CountChip label="Capabilities" value={domain.capabilityCount} narrowHide />
        <CountChip label="Initiatives" value={domain.initiativeCount} />
        <ChevronRight
          className="orion-sm-hide"
          size={16}
          strokeWidth={1.6}
          color={hover ? tokens.gold : tokens.ink4}
        />
      </button>
    </li>
  )
}

function CountChip({
  label,
  value,
  narrowHide,
}: {
  label: string
  value: number
  narrowHide?: boolean
}) {
  return (
    <div
      className={narrowHide ? 'orion-sm-hide' : undefined}
      style={{ textAlign: 'right', minWidth: 92 }}
    >
      <div
        style={{
          fontSize: 18,
          fontWeight: tokens.weightDemi,
          color: tokens.ink,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          fontWeight: tokens.weightMedium,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: tokens.ink3,
        }}
      >
        {label}
      </div>
    </div>
  )
}

function InactiveBadge() {
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: tokens.ink3,
        background: tokens.paper3,
        border: `1px solid ${tokens.line2}`,
        borderRadius: tokens.radiusSm,
        padding: '2px 6px',
      }}
    >
      Inactive
    </span>
  )
}

function ToggleChip({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '6px 12px',
        fontSize: 11,
        fontWeight: tokens.weightDemi,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        fontFamily: tokens.fontDisplay,
        background: active ? tokens.goldWash : 'transparent',
        color: active ? tokens.goldDark : tokens.ink3,
        border: `1px solid ${active ? tokens.goldLine : tokens.line2}`,
        borderRadius: tokens.radiusMd,
        cursor: 'pointer',
        transition: `all ${tokens.durFast} ${tokens.easeStandard}`,
      }}
    >
      {label}
    </button>
  )
}

function EmptyState({ search }: { search: string }) {
  return (
    <div
      style={{
        padding: '48px 24px',
        textAlign: 'center',
        color: tokens.ink3,
        fontFamily: tokens.fontDisplay,
        fontSize: 14,
        background: tokens.paper2,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radiusMd,
      }}
    >
      {search
        ? `No domains match "${search}".`
        : 'No domains yet — they will appear here once seeded.'}
    </div>
  )
}
